import { useContext } from 'react';
import { SidebarContext } from 'src/contexts/SidebarContext';
import Logo from 'src/components/LogoSign';

import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  styled,
  useTheme
} from '@mui/material';
import ChevronLeftTwoToneIcon from '@mui/icons-material/ChevronLeftTwoTone';
import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';

import SidebarMenu from './SidebarMenu';
import { SIDEBAR_EXPANDED_WIDTH } from '../constants';

const SidebarWrapper = styled(Box)(
  ({ theme, ownerState }) => `
        width: ${ownerState.width}px;
        color: ${theme.sidebar.textColor};
        background: ${theme.sidebar.background};
        box-shadow: ${theme.sidebar.boxShadow};
        height: 100%;
        transition: ${theme.transitions.create('width', {
          duration: theme.transitions.duration.shorter
        })};
        
        @media (min-width: ${theme.breakpoints.values.md}px) {
          top: 0;
          left: 0;
          position: fixed;
          z-index: 10;
          border-top-right-radius: ${theme.general.borderRadius};
          border-bottom-right-radius: ${theme.general.borderRadius};
        }
`
);

const TopSection = styled(Box)(
  ({ theme }) => `
        display: flex;
        position: relative;
        height: ${theme.header.height};
        align-items: center;
        padding: ${theme.spacing(0, 1)};
        margin: ${theme.spacing(0, 1)};
        border-bottom: ${theme.sidebar.dividerBg} solid 1px;
`
);

function Sidebar() {
  const {
    sidebarToggle,
    sidebarExpanded,
    closeSidebar,
    toggleSidebarExpanded
  } = useContext(SidebarContext);
  const theme = useTheme();
  const desktopWidth = sidebarExpanded
    ? SIDEBAR_EXPANDED_WIDTH
    : theme.density.sidebarRailWidth;

  const brand = (expanded, mobile = false) => (
    <TopSection sx={{ justifyContent: expanded ? 'space-between' : 'center' }}>
      <Box
        display="flex"
        alignItems="center"
        minWidth={0}
        sx={{ '& a': { margin: 0, flexShrink: 0 } }}
      >
        <Logo />
        {expanded ? (
          <Typography
            noWrap
            sx={{ ml: 1, fontSize: '0.9rem', fontWeight: 800, color: 'text.primary' }}
          >
            AutoClever
          </Typography>
        ) : null}
      </Box>
      {expanded || mobile ? (
        <Tooltip title={mobile ? 'Close navigation' : 'Collapse navigation'}>
          <IconButton
            size="small"
            aria-label={mobile ? 'Close navigation' : 'Collapse navigation'}
            onClick={mobile ? closeSidebar : toggleSidebarExpanded}
            sx={{ width: 30, height: 30, flexShrink: 0 }}
          >
            {mobile ? <CloseTwoToneIcon fontSize="small" /> : <ChevronLeftTwoToneIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Expand navigation" placement="right">
          <IconButton
            size="small"
            aria-label="Expand navigation"
            onClick={toggleSidebarExpanded}
            sx={{ position: 'absolute', right: -2, width: 24, height: 30 }}
          >
            <ChevronRightTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </TopSection>
  );

  return (
    <>
      <SidebarWrapper
        ownerState={{ width: desktopWidth }}
        sx={{
          display: { xs: 'none', md: 'inline-block' },
          overflow: 'hidden'
        }}
      >
        {brand(sidebarExpanded)}
        <Box
          sx={{
            height: `calc(100% - ${theme.header.height})`,
            overflow: 'hidden'
          }}
        >
          <SidebarMenu expanded={sidebarExpanded} />
        </Box>
      </SidebarWrapper>
      <Drawer
        sx={{
          display: { md: 'none', xs: 'inline-block' }
        }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
        PaperProps={{ sx: { width: SIDEBAR_EXPANDED_WIDTH, overflowX: 'hidden' } }}
      >
        <SidebarWrapper ownerState={{ width: SIDEBAR_EXPANDED_WIDTH }}>
          {brand(true, true)}
          <Box sx={{ height: `calc(100% - ${theme.header.height})`, overflow: 'hidden' }}>
            <SidebarMenu expanded />
          </Box>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
